import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  ArrowLeft,
  ChevronDown,
  BookOpen,
  Users,
  Video,
  FileText,
  BarChart2,
  MonitorPlay,
  Activity,
  Home,
  Book,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { getCoursesById } from "../../../services/course.service";
import { useCourse } from "../../../context/CourseContext";
import { useMeetingsV2 } from "../../../context/MeetingV2Context"; 
import LoadingSpinner from "../../../utils/LoadingAnimation";
import MentorInfo from "../../../components/dashboard/utils/MentorInfo";
import CourseInfo from "../../../components/dashboard/utils/CourseInfo";
import WeeklyPlanTable from "../../../components/dashboard/utils/WeeklyInfo";
import SyllabusAccordion from "../../../components/dashboard/utils/SyllabusComponent";
import LectureContent from "../../../components/dashboard/utils/RecordedComponent";
import AssignmentsList from "../../../components/dashboard/utils/AssignmentComponent/AssignmentList";
import CourseDetailsComponents from "../../../components/dashboard/utils/CourseDetailsComponent";
import { TfiAnnouncement } from "react-icons/tfi";
import { FaSignOutAlt, FaUserCircle } from "react-icons/fa";
import { IoIosHome } from "react-icons/io";
import StudentHome from "./course/StudentHome";
import StudentContentSection from "./course/Content/StudentContentSection";
import SelfQuiz from "../teacher/course/Test/SelfQuiz";
import DiscussionForum from "../teacher/course/Discussion";
import { useAuth } from "../../../context/AuthContext";
import { MdLiveTv } from "react-icons/md";
import LecturePanel from "../../lecture/LecturePanel";
import StudentAssignmentSection from "../../Assignment/student/ShowAssignment";
import { VscCommentDiscussion } from "react-icons/vsc";
import ProfileDropdown from "../../../utils/ProfileDropDown";
import AllAnnouncements from "./course/AllAnnouncements.jsx/AllAnnouncements";
import StudentActivitySection from "../../Activity/student/StudentActivitySection";
import { Si1Panel } from "react-icons/si";
import ChatbotWidget from "../../../components/StudentChatbot/ChatbotWidget";
import ComingSoonWrapper from "../../../components/ComingSoonWrapper";


const navigationOptions = {
  home: {
    title: "Home",
    icon: <IoIosHome className="w-5 h-5" />,
  },
  courses: {
    title: "Course",
    icon: <BookOpen className="w-5 h-5" />,
  },
  content: {
    title: "Content",
    icon: <MonitorPlay className="w-5 h-5" />,
    items: [
      { label: "Class Rec.", icon: <Video className="w-5 h-5" /> },
      { label: "E-Learning", icon: <FileText className="w-5 h-5" /> },
    ],
  },
  assessment: {
    title: "Assessment",
    icon: <BarChart2 className="w-5 h-5" />,
    items: [
      { label: "Graded", icon: <FileText className="w-5 h-5" /> },
      { label: "Self Assessment", icon: <FileText className="w-5 h-5" /> },
      { label: "Activity", icon: <Activity className="w-5 h-5" /> },
    ],
  },
  assignment: {
    title: "Discussion",
    icon: <FileText className="w-5 h-5" />,
  },
};

const CourseDetails = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOption, _setSelectedOption] = useState(
    searchParams.get("tab") || "Home"
  );
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [openMobileAccordion, setOpenMobileAccordion] = useState(null);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const { courseID } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { courseData: course, setCourseData } = useCourse();
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const dropdownRefs = useRef({});

  // 2. Get meetings data from the new backend-driven context
  const { getMeetingsForCourse, fetchMeetingsForCourse } = useMeetingsV2();

  // Load meetings for this course
  useEffect(() => {
    if (courseID) {
      fetchMeetingsForCourse(courseID).catch(() => {
        // error is stored in context; component can choose to show a toast if needed
      });
    }
  }, [courseID, fetchMeetingsForCourse]);

  const courseMeetings = getMeetingsForCourse(courseID) || [];

  // Wrapper that keeps the URL ?tab= param in sync
  const setSelectedOption = (option) => {
    _setSelectedOption(option);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (option === "Home") {
        next.delete("tab");
      } else {
        next.set("tab", option);
      }
      return next;
    }, { replace: true });
  };

  // Read legacy query params (section/assignment) from dashboard deep-links
  useEffect(() => {
    const section = searchParams.get('section');
    const assignmentId = searchParams.get('assignment');

    if (section) {
      _setSelectedOption(section);
    }

    if (assignmentId) {
      setSelectedAssignmentId(assignmentId);
    }

    // Convert legacy params to ?tab in a single setSearchParams call
    if (section || assignmentId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (section) {
          next.set('tab', section);
          next.delete('section');
        }
        if (assignmentId) next.delete('assignment');
        return next;
      }, { replace: true });
    }
  }, []); // run once on mount

  // Update current time every minute (used for other time-based UI if needed)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await getCoursesById(courseID);
        const normalizedSyllabus = (() => {
          if (!response?.syllabus) return { modules: [] };
          
          let modules = [];
          if (Array.isArray(response.syllabus)) {
            modules = response.syllabus;
          } else if (Array.isArray(response.syllabus.modules)) {
            modules = response.syllabus.modules;
          }
          
          // Ensure each module has a topics array
          modules = modules.map(module => ({
            ...module,
            topics: Array.isArray(module.topics) ? module.topics : []
          }));
          
          return { modules };
        })();
        setCourseData({ ...response, syllabus: normalizedSyllabus });
      } catch (error) {
        console.error("Error fetching course:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseID, setCourseData]);

  // Add click outside listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown) {
        const currentDropdownRef = dropdownRefs.current[openDropdown];
        if (currentDropdownRef && !currentDropdownRef.contains(event.target)) {
          setOpenDropdown(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  // 3. Check for a currently live meeting for THIS specific course using backend status
  const liveMeeting = useMemo(() => {
    if (!courseID || !courseMeetings.length) return null;
    return courseMeetings.find(
      (meeting) =>
        meeting.course === courseID &&
        meeting.status === "live"
    );
  }, [courseID, courseMeetings]);

  // Handler for joining live class
  const handleJoinLiveClass = () => {
    const activeMeeting = liveMeeting;
    if (activeMeeting) {
      window.open(activeMeeting.link, '_blank', 'noopener,noreferrer');
    } else {
      alert("There is no live class to join at the moment.");
    }
  };


  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  const toggleMobileAccordion = (menu) => {
    setOpenMobileAccordion(openMobileAccordion === menu ? null : menu);
  };

  const handleMobileMenuItemClick = (option) => {
    setSelectedOption(option);
    setIsMobileSidebarOpen(false);
    setOpenMobileAccordion(null);
  };

 const renderDropdown = (menuKey) => {
  if (!navigationOptions[menuKey]) return null;
  const { title, icon, items } = navigationOptions[menuKey];

  // Check if this tab or any of its sub-items are selected
  const isTabSelected = selectedOption === title || (items && items.some(item => selectedOption === item.label));

  if (!items || items.length === 0) {
    return (
      <button
        onClick={() => setSelectedOption(title)}
        className={`relative flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-white/10 dark:hover:bg-white/10 transition-colors ${
          selectedOption === title
            ? "text-accent1"
            : "text-accent1/80"
        }`}
      >
        {icon}
        <span>{title}</span>
        {/* Add line below when selected */}
        {selectedOption === title && (
          <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-full h-1 bg-white rounded-full"></div>
        )}
      </button>
    );
  }

  return (
    <div
      className="relative dropdown-container"
      ref={(el) => (dropdownRefs.current[menuKey] = el)}
    >
      <button
        onClick={() => toggleDropdown(menuKey)}
        className={`relative flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-white/10 dark:hover:bg-white/10 transition-colors ${
          openDropdown === menuKey || isTabSelected
            ? "text-accent1"
            : "text-accent1/80"
        }`}
      >
        {icon}
        <span>{title}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            openDropdown === menuKey ? "rotate-180" : ""
          }`}
        />
        {/* Add line below when this tab or any sub-item is selected */}
        {isTabSelected && (
          <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-full h-1 bg-white rounded-full"></div>
        )}
      </button>

      {openDropdown === menuKey && (
        <div className="absolute left-0 mt-2 w-[440px] bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-xl border border-gray-200 dark:border-gray-600 py-4 z-50">
          <div className="grid grid-cols-2 gap-4 px-4">
            {items.map((item) => {
              const isComingSoon = item.label === "Self Assessment";
              return (
                <ComingSoonWrapper
                  key={item.label}
                  isComingSoon={isComingSoon}
                  featureName={item.label}
                  estimatedRelease="Coming Soon"
                  onClick={() => {
                    if (!isComingSoon) {
                      setSelectedOption(item.label);
                      setOpenDropdown(null);
                    }
                  }}
                >
                  <button
                    onClick={() => {
                      if (!isComingSoon) {
                        setSelectedOption(item.label);
                        setOpenDropdown(null);
                      }
                    }}
                    className={`flex items-center space-x-3 p-4 rounded-lg transition-colors ${
                      selectedOption === item.label
                        ? "bg-accent1/10 dark:bg-accent1/20 text-accent1 dark:text-accent1"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        selectedOption === item.label
                          ? "bg-accent1/20 dark:bg-accent1/30"
                          : "bg-gray-100 dark:bg-gray-700"
                      }`}
                    >
                      {item.icon}
                    </div>
                    <span className="font-medium text-left">{item.label}</span>
                  </button>
                </ComingSoonWrapper>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

  const renderContent = () => {
    switch (selectedOption) {
      case "Home": return <StudentHome setSelectedOption={setSelectedOption} />;
      case "Course": return (
          <div className="w-full mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6 lg:pb-10">
              <div className="flex flex-col gap-6">
                <MentorInfo teacher={course?.teacher} />
                <CourseDetailsComponents course={course} />
                <SyllabusAccordion course={course} />
              </div>
              <div className="flex flex-col gap-6">
                <CourseInfo course={course} />
              </div>
            </div>
            <div className="pt-6">
              <WeeklyPlanTable course={course} />
            </div>
          </div>
        );
      case "Discussion": return <DiscussionForum courseID={courseID} />;
      case "Class Rec.": return <LecturePanel />;
      case "E-Learning": return <StudentContentSection />;
      case "Graded": return <StudentAssignmentSection courseID={courseID} selectedID={selectedAssignmentId || "0"} />;
      case "Self Assessment": return (
          <div className="min-h-[300px] flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-semibold text-primary dark:text-blue-400 mb-2">Coming Soon</div>
              <div className="text-tertiary dark:text-gray-300">Self-assessment quizzes will be available here shortly.</div>
            </div>
          </div>
        );
      case "Activity": return <StudentActivitySection courseID={courseID} selectedID="0" />;
      case "Assignments": return <AssignmentsList />;
      case "Announcements": return <AllAnnouncements />;
      default: return <div className="text-gray-900 dark:text-accent1">Welcome to the Home Section</div>;
    }
  };

  // Render Mobile Sidebar
  const renderMobileSidebar = () => {
    return (
      <>
        {/* Overlay */}
        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[2000] lg:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          ></div>
        )}

        {/* Sidebar */}
        <div
          className={`fixed top-0 left-0 h-full w-72 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out z-[2001] lg:hidden ${
            isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-accent1 dark:text-accent1">Menu</h2>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
          </div>

          {/* Sidebar Content */}
          <div className="overflow-y-auto h-[calc(100vh-73px)] p-4">
            {/* Back Button */}
            <button
              onClick={() => {
                navigate(-1);
                setIsMobileSidebarOpen(false);
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors mb-4 border border-accent1/30 dark:border-accent1/30 bg-accent1/5 dark:bg-accent1/10 text-accent1 dark:text-accent1 hover:bg-accent1/10 dark:hover:bg-accent1/20"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </button>

            {/* Home */}
            <button
              onClick={() => handleMobileMenuItemClick("Home")}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
                selectedOption === "Home"
                  ? "bg-accent1/10 dark:bg-accent1/20 text-accent1 dark:text-accent1"
                  : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              }`}
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Home</span>
            </button>

            {/* Navigation Options with Accordion */}
            {Object.keys(navigationOptions).filter(key => key !== 'home').map((menuKey) => {
              const { title, icon, items } = navigationOptions[menuKey];
              const isTabSelected = selectedOption === title || (items && items.some(item => selectedOption === item.label));

              // If no items, make it a direct click button
              if (!items || items.length === 0) {
                return (
                  <button
                    key={menuKey}
                    onClick={() => handleMobileMenuItemClick(title)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
                      selectedOption === title
                        ? "bg-accent1/10 dark:bg-accent1/20 text-accent1 dark:text-accent1"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {icon}
                    <span className="font-medium">{title}</span>
                  </button>
                );
              }

              return (
                <div key={menuKey} className="mb-2">
                  <button
                    onClick={() => toggleMobileAccordion(menuKey)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                      openMobileAccordion === menuKey || isTabSelected
                        ? "bg-accent1/10 dark:bg-accent1/20 text-accent1 dark:text-accent1"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {icon}
                      <span className="font-medium">{title}</span>
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${
                        openMobileAccordion === menuKey ? "rotate-90" : ""
                      }`}
                    />
                  </button>

                  {/* Accordion Content */}
                  {openMobileAccordion === menuKey && items && items.length > 0 && (
                    <div className="mt-1 ml-4 space-y-1">
                      {items.map((item) => {
                        const isComingSoon = item.label === "Self Assessment";
                        return (
                          <ComingSoonWrapper
                            key={item.label}
                            isComingSoon={isComingSoon}
                            featureName={item.label}
                            estimatedRelease="Coming Soon"
                            onClick={() => {
                              if (!isComingSoon) {
                                handleMobileMenuItemClick(item.label);
                              }
                            }}
                          >
                            <button
                              onClick={() => {
                                if (!isComingSoon) {
                                  handleMobileMenuItemClick(item.label);
                                }
                              }}
                              className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                                selectedOption === item.label
                                  ? "bg-accent1/10 dark:bg-accent1/20 text-accent1 dark:text-accent1"
                                  : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                              }`}
                            >
                              <div className="flex-shrink-0">{item.icon}</div>
                              <span className="text-sm">{item.label}</span>
                            </button>
                          </ComingSoonWrapper>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  if (loading) return <LoadingSpinner />;
  if (!course) return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-gray-900 dark:text-accent1 text-xl">Course not found</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile Sidebar */}
      {renderMobileSidebar()}

      {/* Top Header with Logo, Profile and Logout */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Hamburger Menu Button (Mobile Only) */}
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            </button>

            {/* Profile and Logout on Right */}
            <div className="flex items-center space-x-4 relative z-[1000] ml-auto">
              <abbr title="Announcements">
                <button 
                  className="p-2 rounded-full hover:bg-accent1/20 dark:hover:bg-accent1/20 transition-colors text-accent1/70 dark:text-accent1 hover:text-accent1 dark:hover:text-accent1"
                  onClick={() => setSelectedOption("Announcements")}
                >
                  <TfiAnnouncement size={20} />
                </button>
              </abbr>
              <ProfileDropdown role={"student"} />
              <abbr title="Logout">
                <button
                  className="p-2 rounded-full hover:bg-red/40 dark:hover:bg-red-500/20 transition-colors text-red-600 dark:text-red-400"
                  onClick={() => handleLogout()}
                >
                  <FaSignOutAlt size={22} />
                </button>
              </abbr>
            </div>
          </div>
        </div>
      </header>
      {/* Live Class Buttons */}
      {liveMeeting && (
        <button
          onClick={handleJoinLiveClass}
          className="absolute top-20 lg:top-20 flex justify-center items-center gap-2 right-4 lg:right-8 text-sm lg:text-lg px-4 lg:px-6 py-2 bg-accent1/80 text-accent1 rounded-lg hover:bg-accent1 transition-colors z-[900]"
        >
          <MdLiveTv />
          <span className="hidden sm:inline">Join Live Class</span>
          <span className="sm:hidden">Join Live</span>
        </button>
      )}
      
      {/* Course Header Banner */}
      <div className="p-4 md:p-8 m-auto pt-4 mt-4">
        <div className="flex items-start md:items-center justify-between md:mb-6">
          <div className="flex-1">
            <h1 className="text-2xl md:text-4xl font-bold text-accent1 dark:text-accent1">
              {course.title}
            </h1>
            <p className="text-lg md:text-xl text-accent1/60 dark:text-accent1 mt-2 whitespace-nowrap">
              {course.teacher?.name}
            </p>
          </div>

          {/* Live Class Button */}
          {!liveMeeting && (
            <div className="ml-4 lg:ml-8">
              <button
                disabled
                className="flex justify-center items-center gap-2 text-sm lg:text-lg px-3 lg:px-6 py-1.5 lg:py-2 bg-gray-400 dark:bg-gray-600 text-accent1 dark:text-gray-300 rounded-lg cursor-not-allowed"
              >
                <MdLiveTv className="text-base lg:text-xl" />
                <span className="hidden sm:inline">No Live Class Now</span>
                <span className="sm:hidden">No Live Class</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Bar - Desktop Only */}
      <nav className="hidden lg:block absolute shadow-sm top-0 bg-accent1 dark:bg-accent1/90 w-full z-40 mt-12">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-6 -top-8 z-10 border border-black dark:border-white/20 flex items-center space-x-2 px-4 py-2 bg-white/20 dark:bg-white/10 rounded-lg  hover:bg-white/30 dark:hover:bg-white/20 hover:shadow-md transition-all"
        >
          <ArrowLeft className="h-5 w-5 text-accent1" />
          <span className="text-accent1">Back</span>
        </button>

        <div className="absolute top-40 left-6 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex mx-auto items-center space-x-6">
              {/* Home Button with underline effect */}
              <button
                onClick={() => setSelectedOption("Home")}
                className={`relative flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-white/10 dark:hover:bg-white/10 transition-colors ${
                  selectedOption === "Home"
                    ? "text-accent1"
                    : "text-accent1/80"
                }`}
              >
                <Home className="w-5 h-5" />
                <span>Home</span>
                {/* Add line below when selected */}
                {selectedOption === "Home" && (
                  <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-full h-1 bg-white rounded-full"></div>
                )}
              </button>

              {/* Dropdown Menus */}
              {Object.keys(navigationOptions).filter(key => key !== 'home').map((key) => renderDropdown(key))}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto px-4 sm:px-6 lg:px-8 md:py-8 relative md:mt-8">
        {renderContent()}
      </main>

      {/* Chatbot Widget */}
      <ChatbotWidget courseCode={course.courseCode} />
    </div>
  );
};

export default CourseDetails;
 
